import { NavLink, useLocation } from "react-router";
import { css, cx } from "styled-system/css";
import {
  LayoutDashboard,
  UserCircle,
  Users,
  Inbox,
  Code,
  Bot,
  Settings,
  LogOut,
  Sparkles,
  FileText,
} from "lucide-react";
import { useAuth } from "../../lib/auth";

const navItems = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard },
  { to: "/insights", label: "Insights", icon: Sparkles },
  { to: "/code", label: "Delivery", icon: Code },
  { to: "/developers", label: "Engineers", icon: UserCircle },
  { to: "/teams", label: "Teams", icon: Users },
  { to: "/reporters", label: "Ticket Authors", icon: Inbox },
  { to: "/ai-impact", label: "AI Impact", icon: Bot },
  {
    to: "/reports",
    label: "Reports",
    icon: FileText,
    alsoMatch: ["/cost-capitalization", "/delivery"],
  },
] as Array<{
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  alsoMatch?: string[];
}>;

const bottomNavItems = [{ to: "/settings", label: "Admin", icon: Settings }];

export function Sidebar({ open = false }: { open?: boolean }) {
  const { user, logout } = useAuth();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
  };

  return (
    <aside className={styles.sidebar} data-open={open || undefined}>
      <div className={styles.logo}>
        <span className={styles.logoIcon}>A</span>
        <span className={styles.logoText}>Aura</span>
      </div>

      <nav className={styles.nav}>
        <ul className={styles.navList}>
          {navItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  cx(
                    styles.navItem,
                    (isActive ||
                      item.alsoMatch?.some((p) =>
                        location.pathname.startsWith(p),
                      )) &&
                      styles.navItemActive,
                  )
                }
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>

        <div className={styles.bottomSection}>
          <ul className={styles.navList}>
            {bottomNavItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    cx(styles.navItem, isActive && styles.navItemActive)
                  }
                >
                  <item.icon size={20} />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>

          {/* User info and logout */}
          {user && (
            <div className={styles.userSection}>
              <div className={styles.userInfo}>
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt=""
                    className={styles.userAvatar}
                  />
                ) : (
                  <div className={styles.userAvatarPlaceholder}>
                    {user.name?.[0] || user.email[0]}
                  </div>
                )}
                <div className={styles.userDetails}>
                  <span className={styles.userName}>{user.name || "User"}</span>
                  <span className={styles.userEmail}>{user.email}</span>
                </div>
              </div>
              <button
                className={styles.logoutButton}
                onClick={handleLogout}
                title="Sign out"
              >
                <LogOut size={18} />
              </button>
            </div>
          )}
        </div>
      </nav>
    </aside>
  );
}

const styles = {
  // Editorial shell: paper surface, one hairline rule against the page, a
  // serif nameplate, and ink-on-paper nav with an inverted active state.
  // Fixed column from md up; below md a drawer that slides in over a scrim
  sidebar: css({
    width: "232px",
    bg: "bg.default",
    borderRightWidth: "1px",
    borderRightStyle: "solid",
    borderColor: "fg.default",
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    position: { base: "fixed", md: "sticky" },
    left: 0,
    top: 0,
    zIndex: 50,
    height: "100vh",
    overflowY: "auto",
    transform: { base: "translateX(-100%)", md: "none" },
    transition: "transform 300ms cubic-bezier(0.2, 0, 0, 1)",
    "&[data-open]": { transform: "none" },
  }),
  logo: css({
    display: "flex",
    alignItems: "baseline",
    gap: 2,
    px: 5,
    height: "65px",
    pt: "18px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "border.default",
  }),
  // Small square mark in the signature yellow, the one accent in the shell
  logoIcon: css({
    width: "10px",
    height: "10px",
    bg: "brand.yellow",
    fontSize: "0",
    color: "transparent",
    flexShrink: 0,
    alignSelf: "center",
  }),
  logoText: css({
    fontFamily: "serif",
    fontSize: "26px",
    fontWeight: "700",
    color: "fg.default",
    letterSpacing: "-0.02em",
    lineHeight: "1",
  }),
  nav: css({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    py: 4,
  }),
  navList: css({
    display: "flex",
    flexDirection: "column",
    gap: "1px",
    px: 3,
  }),
  navItem: css({
    display: "flex",
    alignItems: "center",
    gap: 3,
    px: 2,
    py: 2,
    color: "fg.muted",
    fontSize: "13px",
    fontWeight: "600",
    letterSpacing: "0.02em",
    transition: "background 300ms cubic-bezier(0.2, 0, 0, 1), color 300ms cubic-bezier(0.2, 0, 0, 1)",
    cursor: "pointer",
    "& svg": { width: "16px", height: "16px", strokeWidth: 1.75 },
    _hover: {
      bg: "bg.subtle",
      color: "fg.default",
    },
  }),
  // "!" so the active state beats navItem's color and hover whatever order
  // Panda emits the two classes in
  navItemActive: css({
    bg: "fg.default!",
    color: "bg.default!",
    _hover: {
      bg: "fg.default!",
      color: "bg.default!",
    },
  }),
  bottomSection: css({
    display: "flex",
    flexDirection: "column",
    gap: 3,
  }),
  userSection: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    mx: 3,
    px: 2,
    pt: 3,
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderColor: "border.default",
  }),
  userInfo: css({
    display: "flex",
    alignItems: "center",
    gap: 2,
    minWidth: 0,
    flex: 1,
  }),
  userAvatar: css({
    width: 7,
    height: 7,
    borderRadius: "full",
    objectFit: "cover",
    flexShrink: 0,
    filter: "grayscale(1)",
  }),
  userAvatarPlaceholder: css({
    width: 7,
    height: 7,
    borderRadius: "full",
    border: "1px solid",
    borderColor: "fg.default",
    color: "fg.default",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "mono",
    fontSize: "xs",
    fontWeight: "semibold",
    flexShrink: 0,
    textTransform: "uppercase",
  }),
  userDetails: css({
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  }),
  userName: css({
    fontSize: "sm",
    fontWeight: "semibold",
    color: "fg.default",
    truncate: true,
  }),
  userEmail: css({
    fontFamily: "mono",
    fontSize: "11px",
    color: "fg.subtle",
    truncate: true,
  }),
  logoutButton: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    p: 1.5,
    color: "fg.muted",
    bg: "transparent",
    border: "none",
    cursor: "pointer",
    transition: "color 300ms cubic-bezier(0.2, 0, 0, 1)",
    flexShrink: 0,
    _hover: { color: "fg.default" },
  }),
};
